import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Compass, MapTrifold, ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      navigate(res.user.role === "admin" ? "/admin" : "/staff", { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="login-page">
      {/* Left visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-[#09090B] text-white grain-bg">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Compass size={22} weight="duotone" />
            </div>
            <div className="font-display font-black tracking-tight text-xl">FieldOps</div>
          </div>
        </div>
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1774646598677-cc38cb3cac00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMG1hcCUyMHRvcG9ncmFwaHl8ZW58MHx8fHwxNzgyMjUzMzMxfDA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.2) contrast(1.1)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#09090B]/95 via-[#09090B]/60 to-transparent" />
        <div className="relative z-10 max-w-md">
          <div className="overline text-indigo-300 mb-4">Field ops · v1</div>
          <h1 className="font-display font-black tracking-tighter text-5xl leading-none mb-4">
            Every task, every kilometer — accounted for.
          </h1>
          <p className="text-slate-300 leading-relaxed">
            Assign errands, track GPS distance, and see who did what — down to the meter.
          </p>
          <div className="mt-8 flex items-center gap-3 text-xs text-slate-400 font-mono">
            <MapTrifold size={16} /> LIVE GPS TRACKING · KM AUTO-CALCULATION
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md border-slate-200 shadow-none">
          <CardContent className="p-8">
            <div className="overline mb-3">Sign in</div>
            <h2 className="font-display font-bold tracking-tight text-3xl mb-1">Welcome back</h2>
            <p className="text-slate-500 text-sm mb-8">Enter your credentials to continue.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="overline">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="login-email-input"
                  className="mt-2 h-12"
                />
              </div>
              <div>
                <Label htmlFor="password" className="overline">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="mt-2 h-12"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-button"
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 rounded-full font-semibold group"
              >
                {submitting ? "Signing in…" : (<span className="inline-flex items-center gap-2">Sign in <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} /></span>)}
              </Button>
            </form>

            <div className="mt-8 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-xs text-slate-600 font-mono leading-relaxed">
              <div className="overline mb-2 text-slate-500">Seeded accounts</div>
              Admin · <b>admin@fieldops.com</b> / Admin@123<br/>
              Manager · <b>manager@fieldops.com</b> / Manager@123<br/>
              Staff · <b>staff@fieldops.com</b> / Staff@123
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
